import { useMemo } from 'react'
import { Spinner, Typography } from '@material-tailwind/react'
import { useTranslation } from 'react-i18next'
import MediaFileDropzone from '../components/MediaFileDropzone'
import { useBucketInfo } from '../hooks/useBucketInfo'
import { GEOXP_AUDIO_BUCKET, GEOXP_VIDEO_BUCKET, SPOT_MEDIA_TYPES } from '../utils/constants/db.constants'
import { AgamiUploadFormData } from './UploadAgamiInterface'
import AgamiMediaTypeSelector from '../components/AgamiMediaTypeSelector'
import AgamiMediaTypeChip from '../components/AgamiMediaTypeChip'

interface Agami {
  audioUrl?: string
  audio_filename?: string
  audio_content_path?: string
}

interface AgamiSpot {
  content_media_type?: number
}

interface AgamiMediaFileSelectorProps {
  agami?: Agami
  mediaFile: File | null
  setMediaFile: (file: File | null) => void
  formData: AgamiSpot
  handleDataChange: <K extends keyof AgamiUploadFormData>(key: K, newValue: AgamiUploadFormData[K]) => void
  /** V2 renders the one global selector above the language editor. */
  showMediaTypeSelector?: boolean
}

const AgamiMediaFileSelector: React.FC<AgamiMediaFileSelectorProps> = ({
  agami,
  mediaFile,
  setMediaFile,
  formData,
  handleDataChange,
  showMediaTypeSelector = true,
}) => {
  const { t } = useTranslation()
  const editMode = useMemo(() => !!agami, [agami])
  const hasExistingMedia = Boolean(agami?.audio_content_path)

  const mediaType = formData?.content_media_type ?? 1

  const audio = useBucketInfo({
    bucket: GEOXP_AUDIO_BUCKET,
    enabled: mediaType === 1,
  })

  const video = useBucketInfo({
    bucket: GEOXP_VIDEO_BUCKET,
    enabled: mediaType === 2,
  })

  const bucketInfo = mediaType === SPOT_MEDIA_TYPES.AUDIO ? audio : video

  const isLoading = mediaType !== SPOT_MEDIA_TYPES.NONE && bucketInfo.isLoading

  let mediaContent
  if (mediaType === SPOT_MEDIA_TYPES.NONE) {
    mediaContent = <AgamiMediaTypeChip mediaType={mediaType} dataTestClass="intentional-no-media" />
  } else if (isLoading) {
    mediaContent = (
      <div className="w-full flex justify-center items-center py-8">
        <Spinner className="w-6 h-6" />
      </div>
    )
  } else {
    mediaContent = (
      <MediaFileDropzone
        editMode={hasExistingMedia}
        mediaType={mediaType}
        prevFileUrl={agami?.audioUrl}
        prevFileLabel={agami?.audio_filename ?? agami?.audio_content_path}
        mediaFile={mediaFile}
        setMediaFile={setMediaFile}
        uploadSizeLimit={bucketInfo.uploadSizeLimit ?? 0}
        allowedFileTypes={bucketInfo.allowedFileTypes}
      />
    )
  }

  return (
    <div className="w-full h-full flex flex-col gap-4">
      {showMediaTypeSelector && (
        <AgamiMediaTypeSelector
          value={mediaType}
          onChange={(type) => {
            if (type === mediaType) return
            setMediaFile(null)
            handleDataChange('content_media_type', type)
          }}
          allowedTypes={
            !editMode || mediaType !== SPOT_MEDIA_TYPES.NONE
              ? [SPOT_MEDIA_TYPES.AUDIO, SPOT_MEDIA_TYPES.VIDEO]
              : [SPOT_MEDIA_TYPES.AUDIO, SPOT_MEDIA_TYPES.VIDEO, SPOT_MEDIA_TYPES.NONE]
          }
          disabled={editMode && mediaType !== SPOT_MEDIA_TYPES.NONE}
        />
      )}

      <div
        className="w-full h-full flex flex-col bg-white dark:bg-primary rounded-lg p-4 shadow-sm gap-4"
        data-testclass="language-media-card"
      >
        <Typography className="font-bold text-primary text-xl font-barlow">
          {t('upload.mediaFile.title')}
        </Typography>

        {mediaContent}
      </div>
    </div>
  )
}

export default AgamiMediaFileSelector
